/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import ObjectModel from 'cio/models/object';
import ComposerEditableEntity from 'cio/utils/composer/composer-editable-entity';
import CustomerModel from 'cio/models/customer';
import { action } from '@ember/object';
import ObjectTypeModel from 'cio/models/object-type';
import { inject as service } from '@ember/service';
import AnalyticsService from 'cio/services/analytics';

interface Signature {
  Element: HTMLDivElement;
  Args: {
    relatedObjects: ObjectModel[];
    isLiquidInsertionDisabled: boolean;
    editableEntity: ComposerEditableEntity;
    campaign: ComposerEditableEntity['campaign'];
    filterAttribute: unknown;
    insertLiquid: (event: Event | null, liquid: string) => void;
    selectedObjectIndex: number;
    relationshipAttributeMap: Record<string, Record<string, any>>;
    selectedCustomer?: CustomerModel;
    hasReadOnlyPermission: boolean;

    onSelectObject(object: ObjectModel): unknown;
  };
}

export default class MergeObjectRelationshipCampaignViewer extends Component<Signature> {
  @service analytics!: AnalyticsService;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    this.args.onSelectObject(this.relatedObjectsForObjectType[0]);
  }

  get objectType() {
    return this.args.editableEntity.campaign?.objectTypeForCampaign as ObjectTypeModel;
  }

  get relatedObjectsForObjectType() {
    return this.args.relatedObjects.filter((object) => {
      let objectType = object.belongsTo('objectType').value() as ObjectTypeModel;
      return objectType.id === this.objectType.id;
    });
  }

  get objectOptions() {
    return this.relatedObjectsForObjectType.map((object, index) => ({
      value: index,
      object,
      label: object.displayName,
    }));
  }

  get accordionItems() {
    return [
      {
        title: 'Person Attributes',
        attributes: this.personAttributes,
        liquidPath: ['trigger', 'customer'],
      },
      {
        title: 'Relationship Attributes',
        attributes: this.relAttrs,
        isRelAttrs: true,
        liquidPath: ['trigger', 'relationship'],
      },
      {
        title: `${this.objectTypeName} Attributes`,
        attributes: this.objAttrs,
        liquidPath: ['trigger', this.objectType.singularSlug],
      },
    ];
  }

  get currentObject() {
    return this.relatedObjectsForObjectType[this.args.selectedObjectIndex] ?? null;
  }

  get objectTypeName() {
    if (this.objectType == null) {
      return 'Object';
    }

    return this.objectType.singularName;
  }

  get objAttrs() {
    let { currentObject } = this;
    if (currentObject == null) {
      return [];
    }

    return Object.keys(currentObject.attributes).map((key) => ({
      label: key,
      value: currentObject.attributes[key],
    }));
  }

  get relAttrs() {
    let { currentObject } = this;
    let { selectedCustomer, relationshipAttributeMap } = this.args;
    if (currentObject == null) {
      return [];
    }

    let relationshipId = `${selectedCustomer?.id}_${currentObject.id}`;

    if (relationshipAttributeMap[relationshipId] == null) {
      return [];
    }

    return Object.entries(relationshipAttributeMap[relationshipId]).map(([key, value]) => ({
      label: key,
      value,
    }));
  }

  get personAttributes() {
    if (this.args.selectedCustomer?.attrs == null) {
      return [];
    }

    let { attrs } = this.args.selectedCustomer;

    return Object.keys(attrs).map((key) => {
      return {
        label: key,
        value: attrs[key],
      };
    });
  }

  @action
  onObjectChange({ object }: { value: number; object: ObjectModel; label: string }) {
    this.args.onSelectObject(object);
    this.analytics.track('message', 'sample_data_objects_changed');
  }
}
